/**
 * CommunicationPanel Component
 * Topic selection panel for Communication Coach simulations
 */

import { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { MessageSquare } from 'lucide-react';
import { communicationTopics } from './constants';

const TopicButton = ({ topic, highlighted, disabled, onStart }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (highlighted) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [highlighted]);

    return (
    <button
        ref={ref}
        onClick={() => onStart(topic.id)}
        disabled={disabled}
        className={`relative p-4 rounded-xl border-2 hover:border-pink-500/50 hover:bg-pink-500/5 transition-[border-color,background-color] text-left group disabled:opacity-50 ${highlighted ? 'border-violet-500 bg-violet-500/5' : 'border-dashed border-muted-foreground/20'}`}
        data-testid={`comm-topic-${topic.id}`}
    >
        {highlighted && (
            <span className="absolute right-2 top-2 rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid="comm-topic-assigned-badge">Assigned</span>
        )}
        <div className="text-3xl mb-2">{topic.icon}</div>
        <h3 className="font-semibold group-hover:text-pink-500 transition-colors text-sm leading-tight">{topic.name}</h3>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{topic.description}</p>
    </button>
    );
};

const TopicGroup = ({ title, subtitle, topics, testId, ...rest }) => (
    <section className="space-y-3" data-testid={testId}>
        <div>
            <h4 className="text-sm font-semibold">{title}</h4>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {topics.map((topic) => (
                <TopicButton key={topic.id} topic={topic} highlighted={topic.id === rest.highlightTopicId} disabled={rest.disabled} onStart={rest.onStart} />
            ))}
        </div>
    </section>
);

export const CommunicationPanel = ({
    selectedScenario,
    commStep,
    setSelectedScenario,
    loadingScenario,
    startCommunication,
    highlightTopicId,
    assignedOnly
}) => {
    if (selectedScenario?.id !== 'communication' || commStep === 'simulation') {
        return null;
    }
    const groupProps = { highlightTopicId, disabled: loadingScenario === 'communication', onStart: startCommunication };
    const assignedTopic = assignedOnly && communicationTopics.find((t) => t.id === highlightTopicId);
    if (assignedTopic) {
        return (
            <Card className="border-pink-500/30 bg-gradient-to-br from-pink-500/5 to-rose-500/5" data-testid="comm-assigned-topic">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-pink-500" /> Your assigned topic</CardTitle>
                    <CardDescription>Click the topic to start. It begins with a short brief of the facts you need.</CardDescription>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <TopicButton topic={assignedTopic} highlighted disabled={groupProps.disabled} onStart={startCommunication} />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="col-span-full border-pink-500/30 bg-gradient-to-br from-pink-500/5 to-rose-500/5">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-pink-500" />
                    Choose a Practice Topic
                </CardTitle>
                <CardDescription>
                    Select a communication skill to practice. Each topic starts with a short brief of the facts you need.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
                <TopicGroup
                    title="Difficult candidate calls"
                    subtitle="The AI plays a difficult candidate. Stay professional and handle the call."
                    topics={communicationTopics.filter((t) => t.roleplay)}
                    testId="comm-group-roleplay"
                    {...groupProps}
                />
                <TopicGroup
                    title="Coach-led practice"
                    subtitle="The coach sets the scenario and gives tips as you go."
                    topics={communicationTopics.filter((t) => !t.roleplay)}
                    testId="comm-group-coach"
                    {...groupProps}
                />
                <Button variant="ghost" onClick={() => setSelectedScenario(null)} data-testid="comm-back-btn">
                    ← Back to scenarios
                </Button>
            </CardContent>
        </Card>
    );
};
